/* ============================================================================
 * brandData.js — K.O. Command brand library (migration 069).
 *
 * Reads `kc_brand_assets` (the registered, approved brand files Milli's
 * design department composes with) and `kc_design_requests` (the
 * department's queue: what Milli asked for, what came back, what failed).
 *
 * Access model, matching 069 exactly:
 *   - Both tables carry owner-only SELECT policies (`_kc_is_owner()`), so a
 *     plain supabase-js select is enough; a non-owner gets zero rows, not an
 *     error. There is deliberately no write path from this page: assets are
 *     registered by the approval trigger or by hand, never from the UI.
 *   - Files generated by the department live in the private `kc-design`
 *     bucket and are shown through short-lived signed URLs; the canonical
 *     logos and the Milli icons are public files on kocoresystems.com.
 * ========================================================================== */

import { supabase } from '@/lib/supabaseClient';

export const ASSET_KINDS = ['logo', 'graphic', 'render', 'photo', 'video', 'audio', 'document'];

export const REQUEST_STATUSES = [
  'pending',
  'generating',
  'ready',
  'approved',
  'rejected',
  'failed',
];

export const fetchBrandAssets = async () => {
  const { data, error } = await supabase
    .from('kc_brand_assets')
    .select(
      'id,business,name,kind,path,url,width,height,bytes,tags,request_id,notes,created_at,' +
        'poster_path,duration_seconds,video_request_id'
    )
    .order('business', { ascending: true })
    .order('name', { ascending: true });
  if (error) throw error;
  return data || [];
};

export const fetchDesignRequests = async (limit = 20) => {
  const { data, error } = await supabase
    .from('kc_design_requests')
    .select(
      'id,business,kind,purpose,size,variants,template,status,approval_id,' +
        'assets,cost_usd,error,created_at,updated_at'
    )
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data || [];
};

/** Sign every bucket path in one round trip. Returns { [path]: signedUrl }.
 * Never throws: an asset with no picture is still a listed asset. */
export const signAssetPaths = async (paths = [], ttlSeconds = 60 * 60) => {
  const unique = [...new Set(paths.filter(Boolean))];
  if (!unique.length) return {};
  try {
    const { data, error } = await supabase.storage
      .from('kc-design')
      .createSignedUrls(unique, ttlSeconds);
    if (error) throw error;
    return Object.fromEntries(
      (data || []).filter((s) => s?.signedUrl).map((s) => [s.path, s.signedUrl])
    );
  } catch {
    return {};
  }
};

/** The URL to show for an asset: its public URL, or the signed URL for its
 * private path, or null when neither is available. A video shows its poster
 * frame here; the file itself is mediaUrl(). */
export const displayUrl = (asset, signedByPath = {}) => {
  if (asset?.kind === 'video' && asset?.poster_path && signedByPath[asset.poster_path]) {
    return signedByPath[asset.poster_path];
  }
  if (asset?.url) return asset.url;
  if (asset?.path && signedByPath[asset.path]) return signedByPath[asset.path];
  return null;
};

/** The asset's own file (the MP4 for a video), public or signed. */
export const mediaUrl = (asset, signedByPath = {}) => {
  if (asset?.url) return asset.url;
  if (asset?.path && signedByPath[asset.path]) return signedByPath[asset.path];
  return null;
};

/** Every private path an asset needs signed to be shown: the file and, for a
 * video, its poster. */
export const assetPaths = (asset) => [asset?.path, asset?.poster_path].filter(Boolean);

/** Registered notes follow the rulebook's metadata block:
 * "TITLE: … BRAND: … APPROVED USE: … DO NOT: … STATUS: …". Split that into
 * ordered { key, value } rows; anything before the first key (or a note
 * with no keys at all) comes back as a single untitled row. */
export const parseNotes = (notes) => {
  const text = String(notes || '').trim();
  if (!text) return [];
  const re = /(^|\s)([A-Z][A-Z ]{1,30}):\s+/g;
  const rows = [];
  let lastKey = null;
  let lastIndex = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    const start = m.index + m[1].length;
    const before = text.slice(lastIndex, start).trim();
    if (before) rows.push({ key: lastKey, value: before.replace(/\.$/, '') });
    lastKey = m[2].trim();
    lastIndex = start + m[0].length - m[1].length;
  }
  const tail = text.slice(lastIndex).trim();
  if (tail) rows.push({ key: lastKey, value: tail.replace(/\.$/, '') });
  return rows;
};

/** Light-background assets carry a 'light' tag; the thumbnail matches so a
 * cream icon is not judged against a charcoal square. */
export const isLightAsset = (asset) =>
  Array.isArray(asset?.tags) && asset.tags.includes('light');

export const distinctBusinesses = (assets = []) =>
  [...new Set(assets.map((a) => a?.business).filter(Boolean))];

export const filterAssets = (assets = [], { business = null, kind = null } = {}) =>
  assets.filter(
    (a) => (!business || a.business === business) && (!kind || a.kind === kind)
  );

export const formatBytes = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num) || num <= 0) return '';
  if (num < 1024 * 1024) return `${Math.round(num / 1024)} KB`;
  return `${(num / (1024 * 1024)).toFixed(1)} MB`;
};
